import dayjs from "dayjs";
import { capitalize } from "lodash";

import { serverResponseFormat } from "@similarweb/pro-logic-components";
import { colorsPalettes, rgba } from "@similarweb/styles";

import { GraphTemplateV2 } from "app/components/widget/dashboard/GraphTemplate-v2";
import { CHART_COLORS } from "app/constants/ChartColors";
import { getTransformedData } from "app/pages/app-intelligence/app-analysis/pages/Demographics/helpers";
import type { IDemographicsServerResponse } from "app/pages/app-intelligence/app-analysis/pages/Demographics/types";

import { GraphWidget } from "./GraphWidget";

const CATEGORY_DATA_COLOR = colorsPalettes.carbon[300];
const dataKeyToDemographicMap = new Map([
    ["Male", "gender"],
    ["Female", "gender"],
    ["Age18To24", "age"],
    ["Age25To34", "age"],
    ["Age35To44", "age"],
    ["Age45To54", "age"],
    ["Age55Plus", "age"],
]);

export const appChartItemsCreator = (baseItems, keys, keysConfig) => {
    let categoryItemCount = 0;
    return Object.entries(baseItems).map(([itemId, itemData], index) => {
        if (keys?.split(",")?.includes(itemId)) {
            return {
                name: keysConfig.find((k) => k.id === itemId).name,
                id: itemId,
                color: CHART_COLORS.chartMainColors[index],
                data: itemData[itemId],
                visible: true,
                zIndex: 1,
                marker: {
                    symbol: "circle",
                },
            };
        } else {
            categoryItemCount++;
            return {
                categoryBenchmark: true,
                name: capitalize(itemId),
                id: itemId,
                // give each consecutive catgeory series a shade of the initial category color
                color: rgba(CATEGORY_DATA_COLOR, 1 / (categoryItemCount / 10 + 1)),
                lineWidth: 2,
                zIndex: 0,
                data: itemData[itemId],
                visible: true,
                marker: {
                    symbol: "circle",
                },
            };
        }
    });
};

export class AppDemographicsGraphWidget extends GraphWidget {
    constructor() {
        super();
    }

    static getWidgetMetadataType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Data";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    get templateComponent() {
        return GraphTemplateV2;
    }

    get templateUrl() {
        return `/app/components/widget/widget-templates/graph.html`;
    }

    protected validateData(data) {
        return true;
    }

    getViewData() {
        super.getViewData();
        this.viewData.subtitleTextItem = { text: this.apiParams.demographicDataKey };
        return this.viewData;
    }

    public getLegendItems(data?: any) {
        const categoryItems = data ? data.filter((item) => item.categoryBenchmark) : [];
        return data?.length
            ? [...this._widgetConfig.properties.key, ...categoryItems]
            : this._widgetConfig.properties.key;
    }

    private addSeriesNames(chartData) {
        if (!chartData) {
            return;
        }
        chartData.forEach((chartDataItem) => {
            const keyObject = this._widgetConfig.properties.key.find((key) => key.id === chartDataItem.id);
            if (keyObject) {
                chartDataItem.name = keyObject.name;
            }
            if (chartDataItem.categoryBenchmark) {
                chartDataItem.name = `${this._i18nFilter("app.analysis.retention.overtime.legend.category.prefix")} ${
                    chartDataItem.name
                }`;
            }
        });
        return chartData;
    }

    private getchartItems(items) {
        return appChartItemsCreator(items, this.apiParams.keys, this._widgetConfig.properties.key);
    }

    protected async getChartSeries(unorderedData: any, compareItemsKeys: string[]): Promise<any[]> {
        const chartData = this.getchartItems(unorderedData);
        const seriesItemsWithNames = this.addSeriesNames(chartData);
        this.legendItems = this.getLegendItems(seriesItemsWithNames);
        return seriesItemsWithNames;
    }

    private transformResponseDataToDesiredShape(response: IDemographicsServerResponse) {
        const demographicDataKey = this.apiParams.demographicDataKey;
        const responseClone = { ...response, Data: null };
        const { overTimeData, overTimeCategoriesData } = getTransformedData(response);
        const combinedOverTimeData = {
            ...overTimeData[dataKeyToDemographicMap.get(demographicDataKey)],
            ...overTimeCategoriesData[dataKeyToDemographicMap.get(demographicDataKey)],
        };
        responseClone.Data = Object.entries(combinedOverTimeData).reduce((acc, [key, data]) => {
            return {
                ...acc,
                [key]: {
                    [key]:
                        Object.entries(data).map(([date, demoData]) => [
                            dayjs.utc(date, serverResponseFormat).valueOf(),
                            demoData[demographicDataKey] / 100, // this is to turn the data into a percent value
                        ]) ?? [],
                },
            };
        }, {});
        return responseClone;
    }

    public async onNewDataReceived(response: IDemographicsServerResponse): Promise<void> {
        const transformedResponse = this.transformResponseDataToDesiredShape(response);
        await super.onNewDataReceived(transformedResponse);
    }
}
AppDemographicsGraphWidget.register();
